import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { MRT_ColumnDef, MRT_Row, useMaterialReactTable } from 'material-react-table';
import { SettingsTableMain } from 'src/presentations/components/molecules/settings/table/TableMain';
import { ServiceProviderNameAliasDto } from 'src/generated/services/TFinancialApi';
import { useDeleteServiceProviderAlias, useUpsertServiceProviderAlias } from 'src/use-cases/settings/useServiceProvider';
import { openToast } from 'src/use-cases/toast/useToast';
import { newFormatDateString } from 'src/utils/date';
import { Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';

interface ServiceProviderAliasTableProps {
  aliases: ServiceProviderNameAliasDto[];
  serviceProviderId: number;
}

export const ServiceProviderAliasTable = forwardRef<any, ServiceProviderAliasTableProps>(({ aliases, serviceProviderId }, ref) => {
  const [rowToDelete, setRowToDelete] = useState<ServiceProviderNameAliasDto | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const { mutateAsync: deleteAlias, isLoading: isDeleting } = useDeleteServiceProviderAlias();
  // Helper function to clear validation errors
  const clearValidationError = useCallback((fieldName: string) => {
    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
  }, []);
  // Define columns for the Alias table
  const aliasColumns = useMemo<MRT_ColumnDef<ServiceProviderNameAliasDto>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Id',
        size: 100,
        enableEditing: false,
      },
      {
        accessorKey: 'name',
        header: 'Alias',
        size: 300,
        enableEditing: true,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () => clearValidationError('name'),
        },
      },
      {
        accessorKey: 'createdOn',
        header: 'Created On',
        size: 150,
        enableEditing: false,
        accessorFn: (row: ServiceProviderNameAliasDto) => <>{newFormatDateString(row?.createdOn)}</>,
      },
    ],
    []
  );

  const upsertAlias = useUpsertServiceProviderAlias();

  // Simple validation for alias name
  const validateAlias = (values: Partial<ServiceProviderNameAliasDto>) => {
    if (!values.name || values.name.trim() === '') {
      return { name: 'Alias name is required' };
    }
    return {};
  };

  // Handler for creating a new alias
  const handleCreateAlias = async ({ values, exitCreatingMode }: any) => {
    const errors = validateAlias(values);
    if (Object.keys(errors).length > 0) {
      openToast({ toastType: 'error', title: 'Validation Error', description: errors.name });
      return;
    }
    try {
      await upsertAlias.mutateAsync({ serviceProviderId, alias: { name: values.name } });
      openToast({ toastType: 'success', title: 'Success', description: 'Alias created successfully!' });
      exitCreatingMode();
    } catch (error: any) {
      openToast({ toastType: 'error', title: 'Error', description: error?.message || 'Failed to create alias' });
    }
  };

  // Handler for updating an alias
  const handleUpdateAlias = async ({ row, values, exitEditingMode }: any) => {
    const errors = validateAlias(values);
    if (Object.keys(errors).length > 0) {
      openToast({ toastType: 'error', title: 'Validation Error', description: errors.name });
      return;
    }
    try {
      await upsertAlias.mutateAsync({ serviceProviderId, alias: { id: row.original.id, name: values.name } });
      openToast({ toastType: 'success', title: 'Success', description: 'Alias updated successfully!' });
      exitEditingMode();
    } catch (error: any) {
      openToast({ toastType: 'error', title: 'Error', description: error?.message || 'Failed to update alias' });
    }
  };

  const handleDeleteAlias = async (alias: ServiceProviderNameAliasDto) => {
    if (!alias.id) {
      openToast({
        toastType: 'error',
        title: 'Error',
        description: 'Cannot delete alias: missing ID',
      });
      return;
    }

    try {
      await deleteAlias({ aliasId: alias.id });
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Alias deleted successfully!',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    }
  };

  // Render row actions for aliases
  const renderRowActions = useCallback(
    ({ row, table }: { row: MRT_Row<ServiceProviderNameAliasDto>; table: any }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    []
  );

  // Configure the alias table
  const aliasTable = useMaterialReactTable({
    columns: aliasColumns,
    data: aliases || [],
    enableEditing: true,
    editDisplayMode: 'row',
    createDisplayMode: 'row',
    onCreatingRowSave: (props) => {
      void handleCreateAlias(props);
    },
    onEditingRowSave: (props) => {
      void handleUpdateAlias(props);
    },
    enableSorting: true,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 120,
        muiTableHeadCellProps: {
          align: 'center',
        },
      },
    },
    renderEmptyRowsFallback: () => <p className='text-center py-4'>No aliases found</p>,
    renderRowActions,
  });

  useImperativeHandle(ref, () => ({
    startCreatingRow: () => aliasTable.setCreatingRow(true),
    stopCreatingRow: () => aliasTable.setCreatingRow(null),
  }));

  return (
    <div className='overflow-x-auto'>
      <SettingsTableMain table={aliasTable} />
      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isDeleting}
        onAccept={async () => {
          if (rowToDelete) {
            await handleDeleteAlias(rowToDelete);
            setRowToDelete(null);
          }
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure you want to delete this alias?</p>
      </ModalConfirm>
    </div>
  );
});

ServiceProviderAliasTable.displayName = 'ServiceProviderAliasTable';
